import React, { useState } from 'react';
import { observer } from 'mobx-react';

import { Loader } from 'semantic-ui-react';

import classNames from 'classnames';

import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';

import '../../css/BasicIframeModal.less';

/**
 * BasicIframeModal - a lightweight modal component capable of displaying a provided URL within an iframe. Resizable and draggable.
 *
 * @description
 * This modal is intended to be launched via BasicIframeModalLauncherButton or BasicIframeModalLauncherIcon
 * rather than being used directly. The launchers provide a more convenient setup to handle the modal lifecycle automatically.
 *
 * @see BasicIframeModalLauncherButton - For button-triggered modals
 * @see BasicIframeModalLauncherIcon - For floating icon-triggered modals
 */
const BasicIframeModal = observer((props = {}) => {
  const {
    buttonKeyCancelLabel = 'close',
    buttonKeyConfirmLabel = '',
    className = 'BasicIframeModal',
    classNameForGenericModalParent = 'generic-modal-parent-BasicIframeModal',
    closeOnDimmerClick = false,
    closeOnEscape = true,
    disableKeystroke = false,
    disableMove = false,
    disableResize = false,
    initHeight = props.initHeight || Math.floor(window.innerHeight * 0.85),
    initWidth = props.initWidth || Math.floor(window.innerWidth * 0.85),
    minHeight,
    minWidth,
    onOpen = () => {},
    onRequestClose,
    open: isOpen,
    overlay = 'medium',
    top = 75,
    url = '',
    onClose,
    iframeProps = {
      scrolling: 'no' // help prevent double scrollbars inside of iframe
    },
    t
  } = props;

  const headerTitle = props.headerTitle || t('externalContent');

  const [isLoading, setIsLoading] = useState(true);

  const GenericModal = SatCoreComponent('GenericModal');

  const handleClose = async (event, actionButtonData = {}) => {
    setIsLoading(true);

    if (typeof onClose === 'function') {
      await onClose(event, actionButtonData);
    }

    setIsLoading(false);
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    console.error('Failed to load iframe content');
  };

  const renderModalBody = () => {
    if (!url) {
      return (
        <div className='basic-iframe-modal-error'>
          <p>{t('noUrlProvided')}</p>
        </div>
      );
    }

    return (
      <div className='basic-iframe-modal-content'>
        {isLoading && (
          <div className='basic-iframe-modal-loader'>
            <Loader active />
          </div>
        )}
        <iframe
          height='100%'
          onError={handleIframeError}
          onLoad={handleIframeLoad}
          src={url}
          style={{
            border: 'none',
            display: isLoading ? 'none' : 'block',
            overflow: 'hidden' // help prevent double scrollbars inside of iframe
          }}
          title={headerTitle}
          width='100%'
          {...iframeProps}
        />
      </div>
    );
  };

  return (
    <GenericModal
      bodyMsg={renderModalBody}
      buttonKeyCancelLabel={buttonKeyCancelLabel}
      buttonKeyConfirmLabel={buttonKeyConfirmLabel}
      className={classNames(className, {
        'basic-iframe-modal-loading': isLoading,
        'basic-iframe-modal-no-url': !url
      })}
      classNameForGenericModalParent={classNameForGenericModalParent}
      closeOnDimmerClick={closeOnDimmerClick}
      closeOnEscape={closeOnEscape}
      disableConfirmButton={!buttonKeyConfirmLabel}
      disableKeystroke={disableKeystroke}
      disableMove={disableMove}
      disableResize={disableResize}
      headerMsg={headerTitle}
      initHeight={initHeight || minHeight}
      initWidth={initWidth || minWidth}
      isConfirmButtonLoading={false}
      isLoading={false}
      isOpen={isOpen}
      onClose={handleClose}
      onOpen={onOpen}
      onRequestClose={onRequestClose}
      overlay={overlay}
      top={top}
    />
  );
});

export default BasicIframeModal;

SatCoreRegister('BasicIframeModal', BasicIframeModal);
